import { useState } from 'react';
import {
  useMarkAllNotificationMutation,
  useMarkNotificationMutation,
  useNotificationListQuery,
} from 'common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from 'common/errors/error-formatting.ts';
import { useNavigate } from 'react-router-dom';
import { useDrawerStore } from 'store';

export type NotificationsTabIndexTab = 'unread' | 'all';

export type NotificationsStatus = 'read' | 'unread';

export interface NotificationsFilters {
  page: number;
  limit: number;
  status?: NotificationsStatus;
}

export interface NotificationEntries {
  id: string;
  source: 'task';
  status: NotificationsStatus;
  author_name: string | null;
  author_id: string | null;
  description: string;
  task_id: string | null;
  patient_id: string | null;
  created_at: string;
}

const initialFilters: NotificationsFilters = {
  page: 1,
  limit: 10,
  status: 'unread',
};

export const useNotificationPopover = ({
  onClose,
}: {
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<NotificationsFilters>(initialFilters);
  const [tabIndex, setTabIndex] = useState<NotificationsTabIndexTab>('unread');

  const { setDrawerOpen, setTaskId } = useDrawerStore(state => state);

  const {
    mutateAsync: markNotificationAsync,
    isPending: isMarkNotificationLoading,
  } = useMarkNotificationMutation();

  const { mutateAsync: markAllNotifications, isPending: isMarkAllLoading } =
    useMarkAllNotificationMutation();

  const {
    data = {
      notifications: [],
      count: 0,
    },
    isLoading,
  } = useNotificationListQuery({ props: filters });

  const entries: NotificationEntries[] = data.notifications.map(
    notification => ({
      id: notification.id,
      source: notification.source,
      status: notification.status,
      author_name: notification.author_name,
      description: notification.description,
      task_id: notification.task_id,
      patient_id: notification.patient_id,
      created_at: notification.created_at,
      author_id: notification.author_id,
    }),
  );

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newValue: NotificationsTabIndexTab,
  ) => {
    setTabIndex(newValue);
    setFilters(prev => ({
      ...prev,
      page: 1,
      status: newValue === 'unread' ? 'unread' : undefined,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleMarkNotification = async ({
    id,
    variant,
  }: {
    id: string;
    variant: NotificationsStatus;
  }) => {
    if (isMarkNotificationLoading) {
      return;
    }
    try {
      await markNotificationAsync({ id, variant });

      // On the unread tab the notification leaves the list once it is read, so
      // a page holding a single entry would end up empty - step back one page.
      if (
        variant === 'read' &&
        filters.status === 'unread' &&
        filters.page > 1 &&
        entries.length === 1
      ) {
        setFilters(prev => ({ ...prev, page: prev.page - 1 }));
      }
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleMarkAllNotification = async () => {
    try {
      await markAllNotifications();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleNotificationItemClick = async ({
    id,
    variant,
    patient_id,
    task_id,
  }: {
    task_id: string;
    patient_id: string;
    id: string;
    variant: NotificationsStatus;
  }) => {
    if (!patient_id || !task_id) {
      return;
    }
    if (variant === 'read') {
      await markNotificationAsync({ id, variant });
    }
    navigate(`/patient/${patient_id}`);
    setTaskId(task_id);
    setDrawerOpen(true);
    onClose();
  };

  return {
    tabIndex,
    handleChangeTab,
    entries,
    totalCount: data.count,
    handlePageChange,
    handleMarkNotification,
    handleMarkAllNotification,
    isMarkAllLoading,
    handleNotificationItemClick,
    filters,
    isLoading,
  };
};
